// BACKUP COPY - Created before making Step 2 more dynamic
// Date: 2025-09-25
// This is the working version with expandable/retractable criteria sections

import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Chip,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Divider,
  Stack,
  Tooltip,
  Switch,
  FormControlLabel,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import SearchIcon from '@mui/icons-material/Search';
import ExtractIcon from '@mui/icons-material/Description';
import LinkIcon from '@mui/icons-material/Link';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';

const ResearchCriteriaMock = ({ onNext, onBack, uploadedFile }) => {
  const [mustHaves, setMustHaves] = useState('');
  const [niceToHaves, setNiceToHaves] = useState('');
  const [disqualifications, setDisqualifications] = useState('');
  const [urlColumn, setUrlColumn] = useState('website');
  const [enrichmentEnabled, setEnrichmentEnabled] = useState(false);
  const [enrichmentColumns, setEnrichmentColumns] = useState([]);
  const [showHelper, setShowHelper] = useState(false);
  
  // Expansion states - Must-Have expanded by default, others contracted
  const [expandedSections, setExpandedSections] = useState({
    mustHave: true,
    niceToHave: false,
    disqualifications: false
  });

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const addEnrichmentColumn = () => {
    const newColumn = {
      id: Date.now(),
      name: '',
      description: '',
      instructions: '',
      dataFormat: 'text',
      useWebSearch: false,
      estimatedCredits: 1,
    };
    setEnrichmentColumns([...enrichmentColumns, newColumn]);
    setEnrichmentEnabled(true);
  };

  const updateEnrichmentColumn = (id, field, value) => {
    setEnrichmentColumns(columns =>
      columns.map(col =>
        col.id === id
          ? {
              ...col,
              [field]: value,
              estimatedCredits: field === 'useWebSearch' && value ? 3 : 1,
            }
          : col
      )
    );
  };

  const removeEnrichmentColumn = (id) => {
    setEnrichmentColumns(columns => columns.filter(col => col.id !== id));
    if (enrichmentColumns.length === 1) {
      setEnrichmentEnabled(false);
    }
  };

  const calculateTotalCredits = () => {
    const baseCredits = uploadedFile?.rowCount || 500;
    const enrichmentCredits = enrichmentColumns.reduce(
      (sum, col) => sum + (col.estimatedCredits * (uploadedFile?.rowCount || 500)),
      0
    );
    return baseCredits + enrichmentCredits;
  };

  return (
    <Box sx={{ width: '100%' }}>
      {/* STEP 2 Header matching List Cleaner */}
      <Box sx={{ mb: 3 }}>
        <Typography 
          variant="h6" 
          component="h1" 
          gutterBottom
          sx={{ 
            textTransform: 'uppercase',
            letterSpacing: '0.15em',
            fontWeight: 500,
            fontSize: '0.875rem',
            color: '#FFFFFF'
          }}
        >
          STEP 2: CONFIGURE RESEARCH CRITERIA
        </Typography>
      </Box>
      
      {/* 50/50 Layout: Website column selector + data enrichment on left, 3 criteria on right */}
      <Box sx={{ display: 'flex', gap: 3, mb: 3 }}>
        {/* Left side: Website column selector and Data Enrichment */}
        <Box sx={{ flex: 1 }}>
          {/* Website Column Selector */}
          <Paper variant="outlined" sx={{ p: 3, bgcolor: '#161c3e', borderColor: 'rgba(255, 255, 255, 0.1)', mb: 3 }}>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', color: '#FFFFFF' }}>
              <LinkIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
              Website Column
            </Typography>
            
            <FormControl fullWidth>
              <InputLabel>Select column containing website URLs</InputLabel>
              <Select
                value={urlColumn}
                onChange={(e) => setUrlColumn(e.target.value)}
                label="Select column containing website URLs"
              >
                <MenuItem value="website">website</MenuItem>
                <MenuItem value="url">url</MenuItem>
                <MenuItem value="domain">domain</MenuItem>
                <MenuItem value="company_website">company_website</MenuItem>
                <MenuItem value="website_url">website_url</MenuItem>
              </Select>
            </FormControl>
            
            <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
              {uploadedFile?.rowCount || 500} websites will be analyzed from this column
            </Typography>
          </Paper>

          {/* Data Enrichment Section */}
          <Paper variant="outlined" sx={{ p: 3, bgcolor: '#161c3e', borderColor: 'rgba(255, 255, 255, 0.1)' }}>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', color: '#FFFFFF' }}>
              <ExtractIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
              Data Enrichment
              <Chip 
                label="Optional" 
                size="small" 
                sx={{ ml: 2, bgcolor: 'rgba(67, 97, 238, 0.1)', color: 'primary.main', border: '1px solid rgba(67, 97, 238, 0.3)' }} 
              />
            </Typography>
            
            <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
              Add additional data columns to extract from each website
            </Typography>

            {/* Enrichment Columns */}
            {enrichmentColumns.map((column, index) => (
              <Paper key={column.id} variant="outlined" sx={{ 
                p: 2, 
                mb: 2, 
                bgcolor: 'rgba(67, 97, 238, 0.05)',
                borderColor: 'rgba(67, 97, 238, 0.2)'
              }}>
                <Stack spacing={2}>
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Typography variant="subtitle2" sx={{ color: '#FFFFFF' }}>
                      Column {index + 1}
                    </Typography>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={column.useWebSearch}
                          onChange={(e) => updateEnrichmentColumn(column.id, 'useWebSearch', e.target.checked)}
                          size="small"
                        />
                      }
                      label={
                        <Stack direction="row" spacing={1} alignItems="center">
                          {column.useWebSearch ? (
                            <>
                              <SearchIcon fontSize="small" />
                              <Typography variant="body2">Web Search (3 credits)</Typography>
                            </>
                          ) : (
                            <>
                              <ExtractIcon fontSize="small" />
                              <Typography variant="body2">Content Extraction (1 credit)</Typography>
                            </>
                          )}
                        </Stack>
                      }
                    />
                    <Box sx={{ flexGrow: 1 }} />
                    <IconButton 
                      size="small" 
                      color="error"
                      onClick={() => removeEnrichmentColumn(column.id)}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Stack>

                  <Stack direction="row" spacing={2}>
                    <TextField
                      label="Column Name"
                      placeholder="e.g., Employee Count"
                      value={column.name}
                      onChange={(e) => updateEnrichmentColumn(column.id, 'name', e.target.value)}
                      size="small"
                      sx={{ flex: 1 }}
                    />
                    <FormControl size="small" sx={{ minWidth: 120 }}>
                      <InputLabel>Format</InputLabel>
                      <Select
                        value={column.dataFormat}
                        onChange={(e) => updateEnrichmentColumn(column.id, 'dataFormat', e.target.value)}
                        label="Format"
                      >
                        <MenuItem value="text">Text</MenuItem>
                        <MenuItem value="number">Number</MenuItem>
                        <MenuItem value="currency">Currency</MenuItem>
                        <MenuItem value="date">Date</MenuItem>
                        <MenuItem value="boolean">Yes/No</MenuItem>
                      </Select>
                    </FormControl>
                  </Stack>

                  <TextField
                    label="Extraction Instructions"
                    placeholder={
                      column.useWebSearch 
                        ? "e.g., Find the latest employee count from LinkedIn or news articles"
                        : "e.g., Look for employee count on About page or Team page"
                    }
                    value={column.instructions}
                    onChange={(e) => updateEnrichmentColumn(column.id, 'instructions', e.target.value)}
                    size="small"
                    multiline
                    rows={2}
                    fullWidth
                  />
                </Stack>
              </Paper>
            ))}

            {/* Add Column Button */}
            {enrichmentColumns.length < 5 && (
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={addEnrichmentColumn}
                fullWidth
                sx={{ 
                  borderColor: 'rgba(255,255,255,0.3)',
                  color: 'text.secondary',
                  textTransform: 'none',
                  borderRadius: '4px'
                }}
              >
                Add Data Column ({enrichmentColumns.length}/5)
              </Button>
            )}

            {enrichmentColumns.length >= 5 && (
              <Alert severity="info">
                Maximum of 5 enrichment columns reached
              </Alert>
            )}
          </Paper>
        </Box>

        {/* Right side: 3 Criteria sections */}
        <Box sx={{ flex: 1 }}>
          {/* Must-Have Criteria */}
          <Paper variant="outlined" sx={{ p: 3, bgcolor: '#161c3e', borderColor: 'rgba(255, 255, 255, 0.1)', mb: 3 }}>
            <Box 
              sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer', mb: expandedSections.mustHave ? 2 : 0 }}
              onClick={() => toggleSection('mustHave')}
            >
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography variant="h6" sx={{ color: '#FFFFFF' }}>
                  Must-Have Criteria
                </Typography>
                <Chip 
                  label="Required" 
                  size="small" 
                  sx={{ ml: 2, bgcolor: 'rgba(232, 62, 140, 0.1)', color: 'secondary.main', border: '1px solid rgba(232, 62, 140, 0.3)' }} 
                />
                <Tooltip title="These criteria MUST be met for a website to qualify">
                  <HelpOutlineIcon fontSize="small" sx={{ ml: 1, color: 'text.secondary' }} />
                </Tooltip>
              </Box>
              <IconButton size="small" sx={{ color: 'text.secondary' }}>
                {expandedSections.mustHave ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </IconButton>
            </Box>
            {expandedSections.mustHave && (
              <TextField
                fullWidth
                multiline
                rows={6}
                variant="outlined"
                placeholder="Example: Must be a B2B SaaS company&#10;Must have raised Series A or later funding&#10;Must have 50+ employees"
                value={mustHaves}
                onChange={(e) => setMustHaves(e.target.value)}
                helperText={`${mustHaves.length} characters`}
              />
            )}
          </Paper>

          {/* Nice-to-Have Criteria */}
          <Paper variant="outlined" sx={{ p: 3, bgcolor: '#161c3e', borderColor: 'rgba(255, 255, 255, 0.1)', mb: 3 }}>
            <Box 
              sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer', mb: expandedSections.niceToHave ? 2 : 0 }}
              onClick={() => toggleSection('niceToHave')}
            >
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography variant="h6" sx={{ color: '#FFFFFF' }}>
                  Nice-to-Have Criteria
                </Typography>
                <Chip 
                  label="Optional" 
                  size="small" 
                  sx={{ ml: 2, bgcolor: 'rgba(0, 149, 255, 0.1)', color: 'primary.main', border: '1px solid rgba(0, 149, 255, 0.3)' }} 
                />
                <Tooltip title="Preferred features used for ranking qualified results">
                  <HelpOutlineIcon fontSize="small" sx={{ ml: 1, color: 'text.secondary' }} />
                </Tooltip>
              </Box>
              <IconButton size="small" sx={{ color: 'text.secondary' }}>
                {expandedSections.niceToHave ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </IconButton>
            </Box>
            {expandedSections.niceToHave && (
              <TextField
                fullWidth
                multiline
                rows={6}
                variant="outlined"
                placeholder="Example: Preferably based in the US&#10;Ideally uses AWS infrastructure&#10;Bonus if they have API documentation"
                value={niceToHaves}
                onChange={(e) => setNiceToHaves(e.target.value)}
                helperText={`${niceToHaves.length} characters`}
              />
            )}
          </Paper>

          {/* Disqualification Criteria */}
          <Paper variant="outlined" sx={{ p: 3, bgcolor: '#161c3e', borderColor: 'rgba(255, 255, 255, 0.1)' }}>
            <Box 
              sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer', mb: expandedSections.disqualifications ? 2 : 0 }}
              onClick={() => toggleSection('disqualifications')}
            >
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography variant="h6" sx={{ color: '#FFFFFF' }}>
                  Disqualification Criteria
                </Typography>
                <Chip 
                  label="Optional" 
                  size="small" 
                  sx={{ ml: 2, bgcolor: 'rgba(255, 215, 0, 0.1)', color: 'warning.main', border: '1px solid rgba(255, 215, 0, 0.3)' }} 
                />
                <Tooltip title="Automatic exclusion rules regardless of other factors">
                  <HelpOutlineIcon fontSize="small" sx={{ ml: 1, color: 'text.secondary' }} />
                </Tooltip>
              </Box>
              <IconButton size="small" sx={{ color: 'text.secondary' }}>
                {expandedSections.disqualifications ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </IconButton>
            </Box>
            {expandedSections.disqualifications && (
              <TextField
                fullWidth
                multiline
                rows={6}
                variant="outlined"
                placeholder="Example: Exclude consulting firms&#10;No agencies or freelancers&#10;Avoid companies in healthcare"
                value={disqualifications}
                onChange={(e) => setDisqualifications(e.target.value)}
                helperText={`${disqualifications.length} characters`}
              />
            )}
          </Paper>
        </Box>
      </Box>

      {/* Conversational Helper Button */}
      <Box sx={{ mb: 3, textAlign: 'center' }}>
        <Button
          variant="outlined"
          startIcon={<AutoFixHighIcon />}
          onClick={() => setShowHelper(true)}
          sx={{ 
            textTransform: 'none',
            borderColor: 'rgba(255,255,255,0.3)',
            color: 'text.secondary',
            borderRadius: '4px'
          }}
        >
          Help me create better criteria
        </Button>
      </Box>

      {/* Navigation Buttons */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
        <Button 
          onClick={onBack}
          variant="outlined" 
          sx={{ 
            borderColor: 'rgba(255,255,255,0.3)',
            color: 'text.secondary',
            textTransform: 'none',
            borderRadius: '4px'  
          }}
        >
          Previous
        </Button>
        
        <Button 
          variant="contained" 
          color="primary"
          onClick={onNext} 
          disabled={!mustHaves.trim()}
          sx={{ 
            minWidth: 100,
            textTransform: 'none', 
            borderRadius: '4px',
            fontWeight: 'medium',
            px: 3
          }}
        >
          Continue
        </Button>
      </Box>

      {/* Helper Dialog Placeholder */}
      {showHelper && (
        <Alert severity="info" sx={{ mt: 2 }}>
          Conversational Helper would open here to guide criteria creation
        </Alert>
      )}
    </Box>
  );
};

export default ResearchCriteriaMock;